import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container, Row, Col, Card, Button, Form, Spinner, Alert
} from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import TrainingNavbar from '../../components/TrainingNavbar';
import trainingService from '../../services/trainingService';
import { cropAndResizePhoto } from '../../utils/imageUtils';

const EditTrainingSubmission = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const photoInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [submission, setSubmission] = useState(null);
  const [fields, setFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [error, setError] = useState('');

  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [keepExistingPhoto, setKeepExistingPhoto] = useState(true);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await trainingService.getSubmission(id);
      if (!data.success) {
        setError(data.message || 'Failed to load submission');
        return;
      }
      const sub = data.submission;
      setSubmission(sub);
      setFields(sub.fields || []);

      // Pre-fill formData from existing field values
      const init = {};
      (sub.fields || []).forEach(f => { init[f.fieldName] = f.value || ''; });
      setFormData(init);

      if (sub.traineePhoto) setKeepExistingPhoto(true);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load submission');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const blob = await cropAndResizePhoto(file, 105, 119);
      setPhotoFile(blob);
      setKeepExistingPhoto(false);
      const reader = new FileReader();
      reader.onload = (ev) => setPhotoPreview(ev.target.result);
      reader.readAsDataURL(blob);
    } catch (err) {
      alert('Could not process the image. Please try a different photo.');
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const fd = new FormData();
      if (photoFile) fd.append('traineePhoto', photoFile, 'trainee_photo.jpg');
      Object.entries(formData).forEach(([key, val]) => {
        fd.append(`fieldData_${key}`, val || '');
      });

      const result = await trainingService.updateSubmission(id, fd);
      if (result.success) {
        navigate(`/training/submissions/${id}`);
      } else {
        setSaveError(result.message || 'Update failed');
      }
    } catch (e) {
      setSaveError(e.response?.data?.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const renderField = (field) => {
    const value = formData[field.fieldName] || '';
    const commonProps = {
      id: field.fieldName,
      value,
      onChange: (e) => handleFieldChange(field.fieldName, e.target.value),
      placeholder: field.placeholder || '',
      ...(field.fieldName === 'trnNm' ? { maxLength: 30 } : {}),
    };

    switch (field.fieldType) {
      case 'textarea':
        return <Form.Control as="textarea" rows={3} {...commonProps} />;
      case 'select': {
        const options = field.selectOptions
          ? field.selectOptions.split(',').map(o => o.trim()).filter(Boolean)
          : [];
        return (
          <Form.Select {...commonProps}>
            <option value="">— Select —</option>
            {options.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </Form.Select>
        );
      }
      case 'date':
        return <Form.Control type="date" {...commonProps} />;
      case 'number':
        return <Form.Control type="number" {...commonProps} />;
      default:
        return <Form.Control type="text" {...commonProps} />;
    }
  };

  // Group fields by section
  const sectionMap = {};
  fields.forEach(f => {
    const sec = f.sectionName || 'General';
    if (!sectionMap[sec]) sectionMap[sec] = [];
    sectionMap[sec].push(f);
  });

  if (loading) {
    return (
      <>
        <TrainingNavbar />
        <div className="text-center py-5">
          <Spinner animation="border" variant="success" />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <TrainingNavbar />
        <Container className="py-4">
          <Alert variant="danger">{error}</Alert>
          <Button variant="outline-secondary" onClick={() => navigate('/training/coordinator')}>
            Back to Dashboard
          </Button>
        </Container>
      </>
    );
  }

  return (
    <>
      <TrainingNavbar />
      <Container fluid className="py-4">
        <Row className="mb-4 align-items-center">
          <Col>
            <Button variant="link" className="ps-0 text-muted"
              onClick={() => navigate(`/training/submissions/${id}`)}>
              <i className="fas fa-arrow-left me-1"></i> Back to Submission
            </Button>
            <h3 className="mt-1">Edit Training Certificate</h3>
            <p className="text-muted mb-0">
              {submission?.courseName} &mdash; {submission?.certType}
            </p>
          </Col>
          <Col xs="auto" className="d-flex gap-2">
            <Button variant="outline-secondary"
              onClick={() => navigate(`/training/submissions/${id}`)}>
              Cancel
            </Button>
            <Button variant="success" onClick={handleSave} disabled={saving}>
              {saving
                ? <><Spinner size="sm" className="me-2" />Saving…</>
                : <><i className="fas fa-save me-2"></i>Save Changes</>}
            </Button>
          </Col>
        </Row>

        {saveError && (
          <Alert variant="danger" onClose={() => setSaveError('')} dismissible>
            {saveError}
          </Alert>
        )}

        <Alert variant="warning" className="mb-4">
          <i className="fas fa-info-circle me-2"></i>
          After saving, the existing PDF will be cleared. You must regenerate the certificate.
        </Alert>

        <Row>
          <Col lg={8}>
            {Object.entries(sectionMap).map(([sectionName, sectionFields]) => (
              <Card className="shadow-sm mb-4" key={sectionName}>
                <Card.Header>
                  <h6 className="mb-0">{sectionName}</h6>
                </Card.Header>
                <Card.Body>
                  {sectionFields.map(f => (
                    <Form.Group key={f.id} className="mb-3">
                      <Form.Label>
                        {f.fieldLabel}
                        {f.isRequired && <span className="text-danger ms-1">*</span>}
                      </Form.Label>
                      {renderField(f)}
                    </Form.Group>
                  ))}
                </Card.Body>
              </Card>
            ))}
          </Col>

          <Col lg={4}>
            <Card className="shadow-sm mb-4">
              <Card.Header><h6 className="mb-0">Trainee Photo</h6></Card.Header>
              <Card.Body>
                {submission?.traineePhoto && keepExistingPhoto && !photoPreview && (
                  <p className="text-success mb-3">
                    <i className="fas fa-check-circle me-1"></i>
                    Photo already uploaded. Upload a new one to replace it.
                  </p>
                )}
                {photoPreview && (
                  <div className="text-center mb-3">
                    <img src={photoPreview} alt="New preview"
                      style={{ maxWidth: '100%', maxHeight: '180px',
                               borderRadius: '6px', objectFit: 'cover' }} />
                  </div>
                )}
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="w-100"
                  onClick={() => photoInputRef.current?.click()}
                >
                  <i className="fas fa-upload me-2"></i>
                  {submission?.traineePhoto ? 'Replace Photo' : 'Upload Photo'}
                </Button>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handlePhotoChange}
                />
                {photoFile && (
                  <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.85rem' }}>
                    Selected: {photoFile.name}
                  </p>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <div className="d-flex justify-content-end gap-2 mt-2">
          <Button variant="outline-secondary"
            onClick={() => navigate(`/training/submissions/${id}`)}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleSave} disabled={saving}>
            {saving
              ? <><Spinner size="sm" className="me-2" />Saving…</>
              : <><i className="fas fa-save me-2"></i>Save Changes</>}
          </Button>
        </div>
      </Container>
    </>
  );
};

export default EditTrainingSubmission;
